import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Page from '../../components/Layout/Page';
import usePageTitle from '../../hooks/usePageTitle';
import Loader from '../../components/Loader';
import swal from 'sweetalert';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Button from '../../components/Buttons/Button';
import SearchableSelect from '../../components/Inputs/SearchableSelect';

function buildMemberGroups(members) {
  if (!Array.isArray(members)) {
    return [];
  }
  return Object.entries(
    members.reduce((groups, member) => {
      const tag = member.tags ? member.tags[0] + ' - ' + member.type : 'Other';
      if (!groups[tag]) {
        groups[tag] = [];
      }
      groups[tag].push(member);
      return groups;
    }, {}),
  )
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tag, groupMembers]) => ({
      label: tag,
      options: [...groupMembers]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((member) => ({
          value: member.id,
          label: member.name,
        })),
    }));
}

function fixDate(date) {
  if (typeof date !== 'string') {
    return '';
  }
  if (date.length === 10) {
    date = moment(date, 'DD.MM.YYYY').toISOString().substring(0, 16);
  }
  return date;
}

function buildPoiGroups(pois) {
  if (!Array.isArray(pois)) {
    return [];
  }
  return Object.entries(
    pois.reduce((groups, poi) => {
      const type = poi.type || 'Other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(poi);
      return groups;
    }, {}),
  )
    .sort(([a], [b]) => (a === 'football' ? -1 : a.localeCompare(b)))
    .map(([type, groupPois]) => ({
      label: type,
      options: [...groupPois]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((poi) => ({
          value: poi.id,
          label: poi.name,
        })),
    }));
}

const GITLAB_PROJECT_ID = '44';
const GITLAB_BRANCH = 'main';

const CONFIG_FILES = {
  members: 'member.config.json',
  pois: 'poi.config.json',
  events: 'event.config.json',
  teamStadium: 'team.stadium.json',
};

// Resolves to the parsed config, or to null when the file could not be loaded.
// A failing request must not resolve to undefined: GitLab answers an expired
// token with HTTP 401 and a JSON error body, which is not a fetch rejection,
// so without the res.ok check the error payload would flow on as config data.
async function loadConfigFromRepo(filePath) {
  const url = `https://git.marktplatz.io/api/v4/projects/${GITLAB_PROJECT_ID}/repository/files/${filePath}/raw?ref=${GITLAB_BRANCH}`;

  try {
    const res = await fetch(url, {
      headers: {
        'PRIVATE-TOKEN': process.env.REACT_APP_GITLAB_TOKEN,
      },
    });
    if (!res.ok) {
      throw new Error(`GitLab responded with ${res.status} ${res.statusText}`);
    }
    return await res.json();
  } catch (err) {
    console.error(`Could not load ${filePath} from GitLab:`, err);
    return null;
  }
}

// Returns config[key] only when it really is an array, otherwise null.
function readArray(config, key) {
  const value = config && typeof config === 'object' ? config[key] : undefined;
  return Array.isArray(value) ? value : null;
}

function nextMinorVersion(version) {
  const parts = typeof version === 'string' ? version.split('.') : [];
  if (parts.length !== 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return '1.0.0';
  }
  return [parts[0], Number(parts[1]) + 1, 0].join('.');
}

const EventGenerator = () => {
  const { t } = useTranslation();
  usePageTitle('EventGenerator');

  const [members, setMembers] = useState([]);
  const [pois, setPois] = useState([]);
  const [teamStadiumMap, setTeamStadiumMap] = useState(null);

  const [newEvents, setNewEvents] = useState([]);
  const [deletedEvents, setDeletedEvents] = useState([]);

  // const [event, setEvent] = useState({});
  const [, setEventType] = useState('football');

  const [eventList, setEventList] = useState([]);
  const [eventVersion, setEventVersion] = useState('');
  const [failedFiles, setFailedFiles] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [selectedMember1, setSelectedMember1] = useState('');
  const [selectedMember2, setSelectedMember2] = useState('');
  const [selectedPoi, setSelectedPoi] = useState('');

  const memberGroups = useMemo(() => buildMemberGroups(members), [members]);
  const poiGroups = useMemo(() => buildPoiGroups(pois), [pois]);

  function generateGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  const removeEvent = (id) => {
    const events = newEvents.filter((e) => e.id !== id);
    setNewEvents(events);
    localStorage.setItem('generatedEvents', JSON.stringify(events));
  };

  const restoreEvent = (id) => {
    const toRestore = deletedEvents.find((e) => e.id === id);
    const events = [...eventList, toRestore];
    setEventList(events);
    const deleted = deletedEvents.filter((e) => e.id !== id);
    setDeletedEvents(deleted);
    localStorage.setItem('deletedEvents', JSON.stringify(deleted));
  };

  const deleteEvent = (id) => {
    swal({
      title: 'Are you sure?',
      text: 'This will delete the event!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setEventList(eventList.filter((e) => e.id !== id));
        const toDelete = eventList.find((e) => e.id === id);
        const deleted = [...deletedEvents, toDelete];
        setDeletedEvents(deleted);
        localStorage.setItem('deletedEvents', JSON.stringify(deleted));
      }
    });
  };

  const generateEvent = (e) => {
    const name = document.getElementById('name').value.trim();
    const start = document.getElementById('start').value;
    const startTime = document.getElementById('startTime').value;
    const end = document.getElementById('end').value;
    const activeStart = document.getElementById('activeStart').value;
    const activeEnd = document.getElementById('activeEnd').value;
    const poi = selectedPoi;
    const member1 = selectedMember1;
    const member2 = selectedMember2;
    const type = document.getElementById('type').value.trim();
    const tags = document
      .getElementById('tags')
      .value.split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag);

    if (member1 === member2) {
      swal('Error', 'Member 1 and Member 2 cannot be the same.', 'error');
      return;
    }
    if (
      !name ||
      !start ||
      !end ||
      !activeStart ||
      !activeEnd ||
      !poi ||
      !member1 ||
      !member2 ||
      !type
    ) {
      swal('Error', 'Please fill in all required fields.', 'error');
      return;
    }

    if (new Date(start) > new Date(end)) {
      swal('Error', 'Start date cannot be after end date.', 'error');
      return;
    }

    if (new Date(activeStart) > new Date(activeEnd)) {
      swal(
        'Error',
        'Active period start date cannot be after end date.',
        'error',
      );
      return;
    }

    const event = {
      id: generateGUID(),
      name,
      start: start + 'T' + startTime,
      end: end + 'T23:59',
      activePeriod: {
        start: activeStart + 'T00:00',
        end: activeEnd + 'T23:59',
      },
      poi,
      poiObject: pois.find((p) => p.id === poi),
      members: [member1, member2],
      memberObjects: [
        members.find((m) => m.id === member1),
        members.find((m) => m.id === member2),
      ],
      type,
      tags,
    };

    // setEvent(event);
    const events = [...newEvents, event];
    setNewEvents(events);
    localStorage.setItem('generatedEvents', JSON.stringify(events));
  };

  function startChanged(e) {
    const value = e.target.value;
    const end = document.getElementById('end');
    const activeStart = document.getElementById('activeStart');
    const activeEnd = document.getElementById('activeEnd');
    end.value = value;
    activeStart.value = new Date().toISOString().split('T')[0];
    // var temp = new Date(value);
    // temp.setDate(temp.getDate() + 1);
    // activeEnd.value = temp.toISOString().split('T')[0];
    activeEnd.value = value;
    document.getElementById('startTime').value = '00:00';
  }

  const fixLegacyEvents = useCallback(
    (events) => {
      if (!Array.isArray(events)) {
        return [];
      }
      return events.map((e) => {
        e.start = fixDate(e.start);
        e.end = fixDate(e.end);
        e.activePeriod = e.activePeriod || {};
        e.activePeriod.start = fixDate(e.activePeriod.start);
        e.activePeriod.end = fixDate(e.activePeriod.end);
        e.memberObjects = (e.members || []).map((id) =>
          members.find((m) => m.id === id),
        ); // add objects
        e.poiObject = pois.find((poi) => poi.id === e.poi); // add objects
        return e;
      });
    },
    [members, pois],
  );

  const loadEvents = useCallback(() => {
    return loadConfigFromRepo(CONFIG_FILES.events).then((config) => {
      const events = readArray(config, 'events');
      if (!events) {
        setFailedFiles((files) =>
          files.includes(CONFIG_FILES.events)
            ? files
            : [...files, CONFIG_FILES.events],
        );
        return;
      }
      setEventVersion(typeof config.version === 'string' ? config.version : '');
      setEventList(fixLegacyEvents(events));
    });
  }, [fixLegacyEvents]);

  const resetEvents = () => {
    swal({
      title: 'Rest all Events?',
      text: 'Alle new adn deleted Events are lost!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setNewEvents([]);
        setDeletedEvents([]);
        localStorage.removeItem('generatedEvents');
        localStorage.removeItem('deletedEvents');
        if (members.length && pois.length) {
          loadEvents();
        }
      }
    });
  };

  const exportNewEvents = () => {
    const json = {};
    json.version = nextMinorVersion(eventVersion);
    json.events = [...eventList, ...newEvents]
      .map(({ poiObject, memberObjects, ...rest }) => rest)
      .sort((a, b) => (a.id < b.id ? 1 : -1));

    const jsonContent = JSON.stringify(json, null, 2); // Convert to JSON string
    const blob = new Blob([jsonContent], { type: 'application/json' }); // Create a Blob
    const url = URL.createObjectURL(blob); // Generate a URL for the Blob

    const a = document.createElement('a'); // Create a link element
    a.href = url; // Set the URL as the href
    a.download = 'event.config.json'; // Set the filename
    a.click(); // Programmatically click the link to trigger download

    // Clean up
    URL.revokeObjectURL(url); // Revoke the object URL after download
  };

  useEffect(() => {
    try {
      const json = localStorage.getItem('generatedEvents');
      if (json) {
        const parsed = JSON.parse(json);
        setNewEvents(Array.isArray(parsed) ? parsed : []);
      } else {
        setNewEvents([]);
        console.log('No new events found in local storage');
        localStorage.removeItem('generatedEvents');
        localStorage.setItem('generatedEvents', '[]');
      }
    } catch (e) {
      setNewEvents([]);
      localStorage.removeItem('generatedEvents');
      localStorage.setItem('generatedEvents', '[]');
    }

    try {
      const json = localStorage.getItem('deletedEvents');
      if (json) {
        const parsed = JSON.parse(json);
        setDeletedEvents(Array.isArray(parsed) ? parsed : []);
      } else {
        setDeletedEvents([]);
        console.log('No deleted events found in local storage');
        localStorage.removeItem('deletedEvents');
        localStorage.setItem('deletedEvents', '[]');
      }
    } catch (e) {
      setDeletedEvents([]);
      localStorage.removeItem('deletedEvents');
      localStorage.setItem('deletedEvents', '[]');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      loadConfigFromRepo(CONFIG_FILES.members),
      loadConfigFromRepo(CONFIG_FILES.pois),
      loadConfigFromRepo(CONFIG_FILES.teamStadium),
    ]).then(([memberConfig, poiConfig, teamStadiumConfig]) => {
      if (cancelled) {
        return;
      }

      const failed = [];

      const loadedMembers = readArray(memberConfig, 'members');
      if (loadedMembers) {
        setMembers(loadedMembers);
        if (loadedMembers.length > 0) {
          setSelectedMember1(loadedMembers[0].id);
        }
        if (loadedMembers.length > 1) {
          setSelectedMember2(loadedMembers[1].id);
        }
      } else {
        failed.push(CONFIG_FILES.members);
      }

      const loadedPois = readArray(poiConfig, 'pois');
      if (loadedPois) {
        setPois(loadedPois);
        if (loadedPois.length > 0) {
          setSelectedPoi(loadedPois[0].id);
        }
      } else {
        failed.push(CONFIG_FILES.pois);
      }

      if (teamStadiumConfig && typeof teamStadiumConfig === 'object') {
        setTeamStadiumMap(teamStadiumConfig);
      } else {
        failed.push(CONFIG_FILES.teamStadium);
      }

      setFailedFiles(failed);
      setIsLoadingConfig(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!teamStadiumMap || !selectedMember1) {
      return;
    }
    if (!teamStadiumMap[selectedMember1]) {
      return;
    }
    setSelectedPoi(teamStadiumMap[selectedMember1].stadiumId);
  }, [selectedMember1, teamStadiumMap]);

  useEffect(() => {
    if (members.length && pois.length) {
      loadEvents();
    }
  }, [members, pois, loadEvents]);

  if (isLoadingConfig) {
    return (
      <div className="EventGenerator">
        <Page>
          <Loader />
        </Page>
      </div>
    );
  }

  return (
    <div className="EventGenerator">
      <Page>
        {failedFiles.length > 0 && (
          <div className="EventGenerator-LoadError" role="alert">
            <strong>Konfiguration konnte nicht geladen werden.</strong>{' '}
            Betroffen: {failedFiles.join(', ')}. Bitte VPN-Verbindung und{' '}
            <code>REACT_APP_GITLAB_TOKEN</code> prüfen, dann die Seite neu
            laden. Details stehen in der Browser-Konsole.
          </div>
        )}
        <div className="split">
          <div className="left">
            <h1>Event Generator</h1>
            <form id="eventForm">
              <label>
                Event Type:
                <select
                  id="type"
                  name="type"
                  onChange={(e) => setEventType(e.target.value)}
                >
                  <option id="football">football</option>
                  <option id="volleyball">volleyball</option>
                  <option id="basketball">basketball</option>
                  <option id="handball">handball</option>
                </select>
              </label>
              <label>
                Name:
                <input type="text" id="name" required />
              </label>
              <label>
                Start Date:
                <input
                  type="date"
                  id="start"
                  required
                  onChange={startChanged}
                />
              </label>
              <label>
                Start Time:
                <input type="time" id="startTime" required />
              </label>

              <label>
                End Date:
                <input type="date" id="end" required />
              </label>
              <div id="placeholder"></div>
              <label>
                Active Period Start:
                <input type="date" id="activeStart" required />
              </label>
              <label>
                Active Period End:
                <input type="date" id="activeEnd" required />
              </label>
              <SearchableSelect
                id="member1"
                label="Member 1 (Home):"
                groups={memberGroups}
                value={selectedMember1}
                onChange={setSelectedMember1}
                placeholder="Team suchen..."
              />
              <SearchableSelect
                id="member2"
                label="Member 2:"
                groups={memberGroups}
                value={selectedMember2}
                onChange={setSelectedMember2}
                placeholder="Team suchen..."
              />
              <SearchableSelect
                id="poi"
                label="Point of Interest (POI):"
                groups={poiGroups}
                value={selectedPoi}
                onChange={setSelectedPoi}
                placeholder="POI suchen..."
              />

              <label>
                Tags (comma-separated):
                <input type="text" id="tags" defaultValue="dfl" />
              </label>
              <Button shape="action" id="generateEvent" onClick={generateEvent}>
                Generate Event
              </Button>
            </form>
            {/* <hr />
            <div>
              <h2>Generated Event JSON</h2>
              <textarea
                id="output"
                readOnly
                value={JSON.stringify(event, null, 4)}
              ></textarea>
            </div> */}
          </div>

          <div className="right">
            <div className="split">
              <h2>Event Overview</h2>
              <p>***Now loaded directly from gitlab 'main' branch!***</p>
            </div>
            <Button
              id="exportEvents"
              onClick={exportNewEvents}
              disabled={newEvents.length === 0}
            >
              Export Event.Config.JSON
            </Button>{' '}
            <Button shape="warning" id="exportEvents" onClick={resetEvents}>
              Reset all!
            </Button>{' '}
            <br />
            <br />
            <div>
              <div className="EventOverview-Event-List" id="eventOverview">
                {newEvents.length} New Events:
                {newEvents
                  .sort((a, b) => (a.start < b.start ? 1 : -1))
                  .map((event) => {
                    const inPast = moment().isAfter(event.start);
                    return (
                      <div
                        key={event.id}
                        className={`EventOverview-Event-Wrapper ${inPast ? 'inPast' : ''}`}
                      >
                        <div className="EventOverview-Event">
                          <div className="EventOverview-Event-Date">
                            <div className="EventOverview-Event-Date-Date">
                              <div className="EventOverview-Event-Date-Day">
                                {moment(event.start).format('dddd')}
                              </div>
                              <div className="EventOverview-Event-Date-Number">
                                {moment(event.start).format('DD.')}
                              </div>
                              <div className="EventOverview-Event-Date-Month">
                                {moment(event.start).format('MMMM')}
                              </div>
                            </div>
                            <hr />
                            <div className="EventOverview-Event-Date-Time">
                              {moment(event.start).format('HH:mm')}{' '}
                              {t('eventOverview.time')}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Content">
                            <div className="EventOverview-Event-Title">
                              {event.name}
                            </div>
                            <div></div>
                            <div className="EventOverview-Event-POI">
                              <div className="EventOverview-Event-POI-image"></div>
                              {event.poiObject?.name || 'Unknown POI'}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Members">
                            {(event.memberObjects || []).map((member) => (
                              <div
                                key={member?.id}
                                className="EventOverview-Member"
                              >
                                <div className="EventOverview-Member_icon">
                                  <img src={member?.image || ''} alt="logo" />
                                </div>
                                {member?.shortName || 'Unknown Member'}
                              </div>
                            ))}
                          </div>
                          <div className="EventOverview-Event-Members">
                            activePeriod:
                            <div>{event.activePeriod?.start}</div>
                            <div>{event.activePeriod?.end}</div>
                            <div>Type: {event.type}</div>
                          </div>
                          <div className="EventOverview-Event-Buttons">
                            <button
                              type="button"
                              className="deleteButton"
                              onClick={() => removeEvent(event.id)}
                            >
                              Discard
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <br />
              <br />
              <div className="EventOverview-Event-List" id="eventOverview">
                {deletedEvents.length} Deleted Events:
                {deletedEvents
                  .sort((a, b) => (a.start < b.start ? 1 : -1))
                  .map((event) => {
                    const inPast = moment().isAfter(event.start);
                    return (
                      <div
                        key={event.id}
                        className={`EventOverview-Event-Wrapper ${inPast ? 'inPast' : ''}`}
                      >
                        <div className="EventOverview-Event">
                          <div className="EventOverview-Event-Date">
                            <div className="EventOverview-Event-Date-Date">
                              <div className="EventOverview-Event-Date-Day">
                                {moment(event.start).format('dddd')}
                              </div>
                              <div className="EventOverview-Event-Date-Number">
                                {moment(event.start).format('DD.')}
                              </div>
                              <div className="EventOverview-Event-Date-Month">
                                {moment(event.start).format('MMMM')}
                              </div>
                            </div>
                            <hr />
                            <div className="EventOverview-Event-Date-Time">
                              {moment(event.start).format('HH:mm')}{' '}
                              {t('eventOverview.time')}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Content">
                            <div className="EventOverview-Event-Title">
                              {event.name}
                            </div>
                            <div></div>
                            <div className="EventOverview-Event-POI">
                              <div className="EventOverview-Event-POI-image"></div>
                              {event.poiObject?.name || 'Unknown POI'}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Members">
                            {(event.memberObjects || []).map((member) => (
                              <div
                                key={member?.id}
                                className="EventOverview-Member"
                              >
                                <div className="EventOverview-Member_icon">
                                  <img src={member?.image || ''} alt="logo" />
                                </div>
                                {member?.shortName || 'Unknown Member'}
                              </div>
                            ))}
                          </div>
                          <div className="EventOverview-Event-Members">
                            activePeriod:
                            <div>{event.activePeriod?.start}</div>
                            <div>{event.activePeriod?.end}</div>
                            <div>Type: {event.type}</div>
                          </div>
                          <div className="EventOverview-Event-Buttons">
                            <button
                              type="button"
                              className="deleteButton"
                              onClick={() => restoreEvent(event.id)}
                            >
                              Restore
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <br />
              <br />
              <div className="EventOverview-Event-List" id="eventOverview">
                {eventList.length} Existing Events: (Version: {eventVersion})
                {eventList
                  .sort((a, b) => (a.start < b.start ? 1 : -1))
                  .map((event) => {
                    const inPast = moment().isAfter(event.start);
                    return (
                      <div
                        key={event.id}
                        className={`EventOverview-Event-Wrapper ${inPast ? 'inPast' : ''}`}
                      >
                        <div className="EventOverview-Event">
                          <div className="EventOverview-Event-Date">
                            <div className="EventOverview-Event-Date-Date">
                              <div className="EventOverview-Event-Date-Day">
                                {moment(event.start).format('dddd')}
                              </div>
                              <div className="EventOverview-Event-Date-Number">
                                {moment(event.start).format('DD.')}
                              </div>
                              <div className="EventOverview-Event-Date-Month">
                                {moment(event.start).format('MMMM')}
                              </div>
                            </div>
                            <hr />
                            <div className="EventOverview-Event-Date-Time">
                              {moment(event.start).format('HH:mm')}{' '}
                              {t('eventOverview.time')}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Content">
                            <div className="EventOverview-Event-Title">
                              {event.name}
                            </div>
                            <div></div>
                            <div className="EventOverview-Event-POI">
                              <div className="EventOverview-Event-POI-image"></div>
                              {event.poiObject?.name || 'Unknown POI'}
                            </div>
                          </div>
                          <div className="EventOverview-Event-Members">
                            {(event.memberObjects || []).map((member) => (
                              <div
                                key={member?.id}
                                className="EventOverview-Member"
                              >
                                <div className="EventOverview-Member_icon">
                                  <img src={member?.image || ''} alt="logo" />
                                </div>
                                {member?.shortName || 'Unknown Member'}
                              </div>
                            ))}
                          </div>
                          <div className="EventOverview-Event-Members">
                            activePeriod:
                            <div>{event.activePeriod?.start}</div>
                            <div>{event.activePeriod?.end}</div>
                            <div>Type: {event.type}</div>
                          </div>
                          <div className="EventOverview-Event-Buttons">
                            <button
                              type="button"
                              className="deleteButton"
                              onClick={() => deleteEvent(event.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
        <br />
      </Page>
    </div>
  );
};

export default EventGenerator;
